import { getApolloClient } from '@faustwp/core';
import { BlogPostPage } from '../../components/BlogPostPage';
import {
  GET_BLOG_BY_SLUG,
  GET_BLOG_BY_SLUG_MEDIUM,
  GET_BLOG_BY_SLUG_MINIMAL,
  GET_BLOG_POST_BY_SLUG,
  GET_BLOG_POST_BY_SLUG_MEDIUM,
  GET_BLOG_POST_BY_SLUG_MINIMAL,
  GET_BLOG_POST_BY_URI,
  GET_BLOG_POST_BY_URI_MEDIUM,
  GET_BLOG_POST_BY_URI_MINIMAL,
  GET_RELATED_POSTS,
} from '../../lib/queries/blogPost';

function isGraphQLError(err) {
  return err?.graphQLErrors?.length > 0;
}

function postFromData(data) {
  return { post: data?.posts?.nodes?.[0] ?? null, generalSettings: data?.generalSettings ?? null };
}

function blogFromData(data) {
  return { post: data?.blogs?.nodes?.[0] ?? null, generalSettings: data?.generalSettings ?? null };
}

async function fetchPostBySlug(client, slug) {
  const queries = [
    GET_BLOG_POST_BY_SLUG,
    GET_BLOG_POST_BY_SLUG_MEDIUM,
    GET_BLOG_POST_BY_SLUG_MINIMAL,
  ];
  for (const query of queries) {
    try {
      const { data } = await client.query({ query, variables: { slug } });
      const out = postFromData(data);
      if (out.post) return out;
    } catch (_) {}
  }
  return { post: null, generalSettings: null };
}

async function fetchBlogBySlug(client, slug) {
  const queries = [
    GET_BLOG_BY_SLUG,
    GET_BLOG_BY_SLUG_MEDIUM,
    GET_BLOG_BY_SLUG_MINIMAL,
  ];
  for (const query of queries) {
    try {
      const { data } = await client.query({ query, variables: { slug } });
      const out = blogFromData(data);
      if (out.post) return out;
    } catch (_) {}
  }
  return { post: null, generalSettings: null };
}

async function fetchPostByUri(client, uri) {
  const queries = [GET_BLOG_POST_BY_URI, GET_BLOG_POST_BY_URI_MEDIUM, GET_BLOG_POST_BY_URI_MINIMAL];
  for (const query of queries) {
    try {
      const { data } = await client.query({ query, variables: { uri } });
      const node = data?.nodeByUri ?? null;
      if (node?.__typename === 'Post') {
        return { post: node, generalSettings: data?.generalSettings ?? null };
      }
    } catch (_) {}
  }
  return { post: null, generalSettings: null };
}

export default function BlogiPostPage({ post, generalSettings, relatedPosts = [] }) {
  if (!post) return null;
  return (
    <BlogPostPage
      post={post}
      generalSettings={generalSettings}
      relatedPosts={relatedPosts}
      lang="fi"
    />
  );
}

export async function getStaticPaths() {
  return { paths: [], fallback: 'blocking' };
}

export async function getStaticProps(ctx) {
  const { slug } = ctx.params ?? {};
  if (!slug) return { notFound: true, revalidate: 60 };

  const client = getApolloClient();
  let post = null;
  let generalSettings = null;

  try {
    const bySlug = await fetchPostBySlug(client, slug);
    post = bySlug.post;
    generalSettings = bySlug.generalSettings;
  } catch {
    // continue to blogs CPT and URI fallback
  }

  if (!post) {
    try {
      const byBlog = await fetchBlogBySlug(client, slug);
      post = byBlog.post;
      generalSettings = generalSettings ?? byBlog.generalSettings;
    } catch {
      // continue to URI fallback
    }
  }

  if (!post) {
    const uriVariants = [`/blogi/${slug}/`, `/blogi/${slug}`, `blogi/${slug}/`, `blogi/${slug}`];
    for (const uri of uriVariants) {
      const byUri = await fetchPostByUri(client, uri);
      if (byUri.post) {
        post = byUri.post;
        generalSettings = generalSettings ?? byUri.generalSettings;
        break;
      }
    }
  }

  if (!post) return { notFound: true, revalidate: 60 };

  let relatedPosts = [];
  const override = post?.blogPostEn?.relatedPostsOverride ?? post?.blog_post?.related_posts_override;
  const categorySlug = post?.categories?.nodes?.[0]?.slug;
  const excludeId = post?.databaseId;

  if (!override?.length && categorySlug && excludeId) {
    try {
      const { data } = await client.query({
        query: GET_RELATED_POSTS,
        variables: { categorySlug, excludeId },
      });
      relatedPosts = data?.posts?.nodes ?? [];
    } catch {
      // ignore
    }
  }

  return {
    props: { post, generalSettings, relatedPosts },
    revalidate: 60,
  };
}
